<template>
    <!--
        The audiobook's controls (HAP-4591), pinned to the foot of every page
        once a chapter is playing, so the listener can keep browsing the site.
        On a phone it's a compact strip above the app tray; tap it for the
        full-screen player. Gives way to the sermon mini-player: both live at
        the foot of the page, and only one thing plays at a time.
    -->
    <transition name="ab-bar">
        <div v-if="visible" class="ab-theme ab-bar print:hidden" :class="player.book.theme" role="region" aria-label="Audiobook player">
            <!-- phones: progress as a hairline along the top edge -->
            <div class="ab-bar-progress md:hidden" aria-hidden="true"><span :style="{ width: `${fraction * 100}%` }" /></div>

            <div class="ab-bar-inner">
                <!-- now playing: opens the full player -->
                <button type="button" class="ab-bar-now" :aria-label="`Open player: ${t.label}, ${t.title}`" @click="player.sheetOpen = true">
                    <img :src="player.book.cover" alt="" class="ab-bar-cover" />
                    <span class="min-w-0 text-left">
                        <span class="ab-eyebrow block">{{ t.label }}<span v-if="player.waiting" class="ml-1.5 normal-case tracking-normal opacity-70">· loading…</span></span>
                        <span class="ab-bar-title">{{ t.title }}</span>
                        <span class="ab-bar-sub">{{ player.book.title }}</span>
                    </span>
                </button>

                <!-- desktop: full transport + scrubber -->
                <div class="ab-bar-center hidden md:flex">
                    <div class="flex items-center gap-2">
                        <button type="button" class="ab-ctl" aria-label="Previous chapter" title="Previous chapter" :disabled="!canPrev" @click="previous"><player-icon name="prev" class="h-5 w-5" /></button>
                        <button type="button" class="ab-ctl" aria-label="Back 15 seconds" title="Back 15 seconds" @click="skip(-15)"><player-icon name="back15" class="h-6 w-6" /></button>
                        <button type="button" class="ab-ctl-play" :aria-label="player.playing ? 'Pause' : 'Play'" @click="toggle">
                            <player-icon :name="player.playing ? 'pause' : 'play'" class="h-5 w-5" :class="{ 'ml-0.5': !player.playing }" />
                        </button>
                        <button type="button" class="ab-ctl" aria-label="Forward 30 seconds" title="Forward 30 seconds" @click="skip(30)"><player-icon name="fwd30" class="h-6 w-6" /></button>
                        <button type="button" class="ab-ctl" aria-label="Next chapter" title="Next chapter" :disabled="!canNext" @click="next"><player-icon name="next" class="h-5 w-5" /></button>
                    </div>
                    <seek-bar class="mt-0.5 max-w-xl" />
                </div>

                <!-- desktop: speed, sleep, chapters, expand, close -->
                <div class="ab-bar-right hidden md:flex">
                    <button type="button" class="ab-speed" :aria-label="`Playback speed ${player.rate}×. Change`" title="Playback speed" @click="cycleRate">{{ player.rate }}×</button>
                    <sleep-timer />
                    <router-link :to="`/audiobooks/${player.slug}`" class="ab-ctl" aria-label="All chapters" title="All chapters"><player-icon name="list" class="h-5 w-5" /></router-link>
                    <button type="button" class="ab-ctl" aria-label="Open the full player" title="Full player" @click="player.sheetOpen = true"><player-icon name="up" class="h-5 w-5" /></button>
                    <button type="button" class="ab-ctl" aria-label="Stop and close the player" title="Close" @click="dismiss"><i class="fal fa-times text-lg" aria-hidden="true" /></button>
                </div>

                <!-- phones: back 15 + play -->
                <div class="flex shrink-0 items-center gap-1 md:hidden">
                    <button type="button" class="ab-ctl" aria-label="Back 15 seconds" @click="skip(-15)"><player-icon name="back15" class="h-6 w-6" /></button>
                    <button type="button" class="ab-ctl-play" :aria-label="player.playing ? 'Pause' : 'Play'" @click="toggle">
                        <player-icon :name="player.playing ? 'pause' : 'play'" class="h-5 w-5" :class="{ 'ml-0.5': !player.playing }" />
                    </button>
                </div>
            </div>
        </div>
    </transition>
    <now-playing />
</template>

<script>
import { player, toggle, skip, previous, next, neighbour, setRate, dismiss, RATES, resumeLastBook } from '../../helpers/audiobook'
import PlayerIcon from './playerIcon.vue'
import SeekBar from './seekBar.vue'
import SleepTimer from './sleepTimer.vue'
import NowPlaying from './nowPlaying.vue'
import './audiobook.css'

export default {
    name: 'PlayerBar',
    components: { PlayerIcon, SeekBar, SleepTimer, NowPlaying },
    inject: ['store'],
    data() {
        return { player }
    },
    computed: {
        t() { return player.current },
        // On the book's page as soon as a chapter is cued; elsewhere once the
        // listener has pressed play, so the site isn't cluttered.
        visible() {
            if (!player.current || !player.book) return false
            if (this.store.nowPlaying?.id) return false // the sermon mini-player has the foot of the page
            return player.started || this.$route.path === `/audiobooks/${player.slug}`
        },
        fraction() { return player.duration ? player.position / player.duration : 0 },
        canPrev() { return player.position > 5 || !!neighbour(-1) },
        canNext() { return !!neighbour(1) },
    },
    watch: {
        // Tell the page there's a bar to leave room for (index.css .has-audiobook-bar).
        visible(v) { document.documentElement.classList.toggle('has-audiobook-bar', v) },
        '$route.path'() { player.sheetOpen = false },
        // A sermon or podcast handed off to the mini-player: stop the book.
        'store.nowPlaying.id'(id) { if (id) dismiss() },
    },
    // client only: App.vue is server-rendered too
    mounted() {
        resumeLastBook()
    },
    methods: {
        toggle, skip, previous, next, dismiss,
        cycleRate() { setRate(RATES[(RATES.indexOf(player.rate) + 1) % RATES.length]) },
    },
}
</script>

<style>
.ab-bar {
    position: fixed; left: 0; right: 0; bottom: var(--app-tray-h, 0px); z-index: 52;
    border-top: 1px solid var(--ab-line-strong);
    background: color-mix(in srgb, var(--ab-bg) 95%, transparent);
    -webkit-backdrop-filter: blur(12px); backdrop-filter: blur(12px);
    box-shadow: 0 -6px 24px -12px rgba(60, 30, 30, 0.25);
    padding-left: env(safe-area-inset-left); padding-right: env(safe-area-inset-right);
    color: var(--ab-ink);
}
@media (min-width: 768px) { .ab-bar { padding-bottom: env(safe-area-inset-bottom); } }
.ab-bar-progress { position: absolute; left: 0; right: 0; top: 0; height: 2px; background: var(--ab-line); }
.ab-bar-progress span { display: block; height: 100%; background: var(--ab-gold); transition: width 0.5s linear; }
.ab-bar-inner { margin: 0 auto; display: flex; height: 4rem; max-width: 72rem; align-items: center; gap: 0.75rem; padding: 0 0.75rem; }
@media (min-width: 768px) { .ab-bar-inner { height: 84px; gap: 1.5rem; padding: 0 1.5rem; } }
.ab-bar-now { display: flex; min-width: 0; flex: 1 1 0%; align-items: center; gap: 0.75rem; border-radius: 0.5rem; padding: 0.25rem 0.5rem 0.25rem 0; }
@media (min-width: 768px) { .ab-bar-now { width: 18rem; flex: none; } }
.ab-bar-cover { height: 3rem; width: auto; flex-shrink: 0; border-radius: 2px; box-shadow: 0 2px 6px rgba(0, 0, 0, 0.25); }
@media (min-width: 768px) { .ab-bar-cover { height: 3.5rem; } }
.ab-bar-title { display: block; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; font-family: ui-serif, Georgia, serif; font-size: 1rem; font-weight: 600; line-height: 1.25; color: var(--ab-ink); }
.ab-bar-sub { display: none; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; font-family: ui-serif, Georgia, serif; font-size: 0.75rem; font-style: italic; color: var(--ab-muted); }
@media (min-width: 768px) { .ab-bar-sub { display: block; } }
.ab-bar-center { min-width: 0; flex: 1 1 0%; flex-direction: column; align-items: center; }
.ab-bar-right { width: 18rem; flex-shrink: 0; align-items: center; justify-content: flex-end; gap: 0.25rem; }
.ab-bar-enter-active, .ab-bar-leave-active { transition: transform 0.25s ease, opacity 0.25s ease; }
.ab-bar-enter-from, .ab-bar-leave-to { transform: translateY(100%); opacity: 0; }
</style>
